import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  HStack,
  VStack,
  Text,
  Button,
  Link,
  Icon,
  Slide,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Switch,
  Badge,
  Divider,
} from '@chakra-ui/react';
import { FaCookieBite, FaCog, FaShieldAlt, FaChartBar, FaBullhorn, FaCheck } from 'react-icons/fa';

const CookieBanner = () => {
  const { isOpen: isBannerOpen, onOpen: onBannerOpen, onClose: onBannerClose } = useDisclosure();
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const [hasConsent, setHasConsent] = useState(true);
  
  const [cookiePreferences, setCookiePreferences] = useState({
    essential: true,
    analytics: true,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setHasConsent(false);
      onBannerOpen();
    }
  }, [onBannerOpen]);

  const handleToggle = (cookieType) => {
    if (cookieType === 'essential') return;
    setCookiePreferences(prev => ({
      ...prev,
      [cookieType]: !prev[cookieType]
    }));
  };

  const saveConsent = (preferences) => {
    const consent = {
      preferences,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    if (typeof window.updateConsentState === 'function') window.updateConsentState(preferences);
    setHasConsent(true);
    onBannerClose();
    onModalClose();
  };

  const acceptAll = () => {
    saveConsent({
      essential: true,
      analytics: true,
      marketing: true,
    });
  };

  const acceptEssentialOnly = () => {
    saveConsent({
      essential: true,
      analytics: false,
      marketing: false,
    });
  };

  const saveCustomPreferences = () => {
    saveConsent(cookiePreferences);
  };

  const openCustomizeModal = () => {
    onModalOpen();
  };

  const cookieCategories = [
    {
      id: 'essential',
      title: 'Essential Cookies',
      icon: FaShieldAlt,
      color: 'green',
      required: true,
      description: 'Required for the website to function. Cannot be disabled.',
    },
    {
      id: 'analytics',
      title: 'Analytics Cookies',
      icon: FaChartBar,
      color: 'purple',
      required: false,
      description: 'Help us understand how visitors use our platform.',
    },
    {
      id: 'marketing',
      title: 'Marketing Cookies',
      icon: FaBullhorn,
      color: 'orange',
      required: false,
      description: 'Used to deliver relevant advertisements.',
    },
  ];

  if (hasConsent) return null;

  return (
    <>
      {/* Bottom Banner */}
      <Slide direction="bottom" in={isBannerOpen} style={{ zIndex: 9999 }}>
        <Box
          bg="gray.900"
          color="white"
          py={4}
          px={4}
          shadow="dark-lg"
          borderTop="3px solid"
          borderColor="brand.500"
        >
          <Container maxW="container.xl">
            <HStack
              justify="space-between"
              align="center"
              flexWrap={{ base: 'wrap', md: 'nowrap' }}
              gap={4}
            >
              <HStack spacing={3} flex="1">
                <Icon as={FaCookieBite} boxSize={6} color="brand.400" />
                <VStack align="start" spacing={0}>
                  <Text fontWeight="semibold" fontSize="sm">
                    We use cookies to enhance your experience
                  </Text>
                  <Text fontSize="xs" color="gray.400">
                    We use cookies to provide essential functionality, analyze usage, and improve our services.{' '}
                    <Link as={RouterLink} to="/privacy" color="brand.400" textDecoration="underline">
                      Privacy Policy
                    </Link>
                  </Text>
                </VStack>
              </HStack>

              <HStack spacing={3} flexWrap="wrap" justify={{ base: 'center', md: 'flex-end' }}>
                <Button
                  variant="ghost"
                  size="sm"
                  color="gray.300"
                  leftIcon={<FaCog />}
                  _hover={{ bg: 'gray.700' }}
                  onClick={openCustomizeModal}
                >
                  Customize
                </Button>
                <Button
                  variant="outline"
                  size="sm"                  
                  color="brand.300"
                  onClick={acceptEssentialOnly}
                  _hover={{ bg: 'gray.700' }}
                >
                  Essential Only
                </Button>
                <Button
                  colorScheme="brand"
                  size="sm"
                  onClick={acceptAll}
                >
                  Accept All
                </Button>
              </HStack>
            </HStack>
          </Container>
        </Box>
      </Slide>

      {/* Customize Modal */}
      <Modal isOpen={isModalOpen} onClose={onModalClose} size="lg" isCentered scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent mx={4}>
          <ModalHeader>
            <HStack spacing={3}>
              <Icon as={FaCookieBite} boxSize={6} color="brand.500" />
              <Text>Cookie Preferences</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          
          <ModalBody>
            <Text fontSize="sm" color="gray.600" mb={4}>
              Manage your cookie preferences. Essential cookies are required for the website to function properly.
            </Text>
            
            <VStack spacing={4} align="stretch">
              {cookieCategories.map((category) => (
                <Box
                  key={category.id}
                  p={4}
                  border="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  bg={cookiePreferences[category.id] ? `${category.color}.50` : 'gray.50'}
                >
                  <HStack justify="space-between" align="start">
                    <HStack spacing={3} align="start" flex={1}>
                      <Box
                        p={2}
                        borderRadius="md"
                        bg={`${category.color}.100`}
                      >
                        <Icon as={category.icon} boxSize={4} color={`${category.color}.500`} />
                      </Box>
                      <VStack align="start" spacing={1} flex={1}>
                        <HStack>
                          <Text fontWeight="semibold" fontSize="sm">
                            {category.title}
                          </Text>
                          {category.required && (
                            <Badge colorScheme="green" fontSize="xs">
                              Required
                            </Badge>
                          )}
                        </HStack>
                        <Text fontSize="xs" color="gray.600">
                          {category.description}
                        </Text>
                      </VStack>
                    </HStack>
                    <Switch
                      colorScheme="brand"
                      size="md"
                      isChecked={cookiePreferences[category.id]}
                      onChange={() => handleToggle(category.id)}
                      isDisabled={category.required}
                    />
                  </HStack>
                </Box>
              ))}
            </VStack>
            
            <Divider my={4} />
            
            <Text fontSize="xs" color="gray.500">
              For more information, see our{' '}
              <Link as={RouterLink} to="/privacy" color="brand.500">
                Privacy Policy
              </Link>.
            </Text>
          </ModalBody>

          <ModalFooter borderTop="1px" borderColor="gray.200">
            <HStack spacing={3} w="full" justify="space-between">
              <Button variant="ghost" size="sm" onClick={acceptEssentialOnly}>
                Essential Only
              </Button>
              <HStack spacing={3}>
                <Button variant="outline" size="sm" onClick={acceptAll}>
                  Accept All
                </Button>
                <Button
                  colorScheme="brand"
                  size="sm"
                  leftIcon={<FaCheck />}
                  onClick={saveCustomPreferences}
                >
                  Save Preferences
                </Button>
              </HStack>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default CookieBanner;